import { useContext, useMemo } from 'react';

import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { UserRoles } from 'app/constants/user';
import {
  isNoaAuditor,
  isNoaProcessor,
  isNoaProcessorManager,
  isPayeeAdmin,
  isPayeeBilling,
  isPayeeDriver,
  isTriumphInternal,
} from 'app/utils/lib/user';

import { AuthDispatchContext, AuthStateContext } from './contexts';

export const useAuthDispatch = () =>
  useContext(AuthDispatchContext);

export const useAuthState = () =>
  useContext(AuthStateContext);

export const useIsRole = (roleId: UserRoles) => {
  const { authRoles } = useAuthState();

  return useMemo(() => authRoles.includes(roleId),
    [authRoles, roleId]);
};

export const useIsInAnyRole = (roleIds: UserRoles[]) => {
  const { authRoles } = useAuthState();

  return useMemo(() => authRoles.some(roleId => roleIds.includes(roleId)),
    [authRoles, roleIds]);
};

/* [Kyle 3/24/22]: internal users are given the role of USER_ROLES.USER_ADMIN and USER_ROLES.PAYEE_ADMIN automatically.
Therefore, they must be checked against internal roles only. */
export const useCanManageUsers = () => {
  const isInternal = useIsTriumphInternal();

  const canCreateUserRoles = useMemo(() => {
    return isInternal
      ? [
        UserRoles.AccountExecutive,
        UserRoles.IntegrationManager,
        UserRoles.OpsManager,
      ]
      : [
        UserRoles.PayeeAdmin,
        UserRoles.UserAdmin,
      ];
  },
  [isInternal]);

  return useIsInAnyRole(canCreateUserRoles);
};

export const useCanResetUserMfa = () => {
  const isInternal = useIsTriumphInternal();

  const canResetMfa = useMemo(() => {
    return isInternal
      ? [
        UserRoles.OpsManager,
        UserRoles.AccountExecutive,
        UserRoles.Csr,
        UserRoles.IbexCsr,
      ]
      : [
        UserRoles.PayeeAdmin,
        UserRoles.UserAdmin,
      ];
  },
  [isInternal]);

  return useIsInAnyRole(canResetMfa);
};

export const useHasActiveCompany = () => {
  const { tokenData } = useAuthState();

  return tokenData?.TId ? Number(tokenData.TId) > 0 : false;
};

export const useIsTriumphInternal = () => {
  const { authRoles } = useAuthState();

  return useMemo(() => isTriumphInternal(authRoles),
    [authRoles]);
};

export const useIsPayeeAdmin = () => {
  const user = useGetUser();

  return useMemo(() => isPayeeAdmin(user),
    [user]);
};

export const useIsPayeeBilling = () => {
  const user = useGetUser();

  return useMemo(() => isPayeeBilling(user),
    [user]);
};

export const useIsPayeeDriver = () => {
  const user = useGetUser();

  return useMemo(() => isPayeeDriver(user),
    [user]);
};

export const useIsNoaIbex = () => {
  const { authRoles } = useAuthState();

  return useMemo(() => (isNoaProcessor(authRoles) ||
    isNoaProcessorManager(authRoles) ||
    isNoaAuditor(authRoles))
  && !isTriumphInternal(authRoles),
  [authRoles]);
};

export const useIsNoaProcessorManager = () => {
  const { authRoles } = useAuthState();

  return useMemo(() => (isNoaProcessorManager(authRoles)),
    [authRoles]);
};

export const useIsPayeeAndOnlyDriver = () => {
  const userIsPayeeAdmin = useIsPayeeAdmin();
  const userIsPayeeBilling = useIsPayeeBilling();
  const userIsPayeeDriver = useIsPayeeDriver();

  return userIsPayeeDriver && !userIsPayeeAdmin && !userIsPayeeBilling;
};
